import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import { Core } from "@faire/design-tokens";
import { getRetailerOrBrandToken } from "@faire/help-center-utils/serialized-data/getRetailerOrBrandToken";
import { trackCxFoundationsTellUsMorePageNextButtonClick } from "@faire/web-api/events/cxFoundations/click/tellUsMorePageNextButton";
import { trackCxFoundationsTellUsMoreView } from "@faire/web-api/events/cxFoundations/view/tellUsMore";
import { StringContextType } from "@faire/web-api/faire/translator/StringContextType";
import { Button } from "@faire/web/slate/Button";
import { Column } from "@faire/web/slate/Layout";
import { Spacer } from "@faire/web/slate/spacing";
import { Typography } from "@faire/web/slate/Typography";
import { useViewport } from "@faire/web/ui/hooks/useViewport";
import { StrictLocalMsg } from "@faire/web/ui/lib/localization";
import React, { useCallback, useEffect, useRef } from "react";
import styled from "styled-components";
import { BackButton } from "../../components/BackButton";
import { IssueTopics } from "../../components/IssueTopics";
import { useNavigationStore } from "../../hooks/NavigationContext";
import { useSelfServeContextStore } from "../../hooks/SelfServeContext";
import { useCurrentNodeHasMoreLevels } from "../../hooks/useCurrentNodeHasMoreLevels";
import { useIssueType } from "../../hooks/useIssueType";
import { useLevelNodesSectionTitles } from "../../hooks/useLevelNodesSectionTitles";
import { useNavigation } from "../../hooks/useNavigation";
import { useNodeSetter } from "../../hooks/useNodeSetter";
import { useSelectedNode } from "../../hooks/useSelectedNode";
import { useTrackPageViewEffect } from "../../hooks/useTrackPageViewEffect";
import { LevelNodesSection } from "./LevelNodesSection";
export var MoreIssue = function MoreIssue() {
  useTrackPageViewEffect(trackCxFoundationsTellUsMoreView);
  var _useSelectedNode = useSelectedNode(),
    level1Node = _useSelectedNode.level1Node,
    level2Node = _useSelectedNode.level2Node,
    level3Node = _useSelectedNode.level3Node,
    level4Node = _useSelectedNode.level4Node,
    level5Node = _useSelectedNode.level5Node;
  var _useNodeSetter = useNodeSetter(),
    setLevel2Node = _useNodeSetter.setLevel2Node,
    setLevel3Node = _useNodeSetter.setLevel3Node,
    setLevel4Node = _useNodeSetter.setLevel4Node,
    setLevel5Node = _useNodeSetter.setLevel5Node;
  var _useSelfServeContextS = useSelfServeContextStore(["brandOrders", "selectedBrandOrder"]),
    _useSelfServeContextS2 = _slicedToArray(_useSelfServeContextS, 1),
    _useSelfServeContextS3 = _useSelfServeContextS2[0],
    brandOrders = _useSelfServeContextS3.brandOrders,
    selectedBrandOrder = _useSelfServeContextS3.selectedBrandOrder;
  var _useNavigation = useNavigation(),
    goToNextStep = _useNavigation.goToNextStep;
  var _useIssueType = useIssueType(),
    issueType = _useIssueType.issueType;
  var sectionTitles = useLevelNodesSectionTitles();
  var goToNextStepRef = useRef(goToNextStep);
  var bottomElementRef = useRef(null);
  var _useViewport = useViewport(),
    isMobile = _useViewport.isMobile;
  var _useNavigationStore = useNavigationStore(["hasLevelQueryParams"]),
    _useNavigationStore2 = _slicedToArray(_useNavigationStore, 2),
    hasLevelQueryParams = _useNavigationStore2[0].hasLevelQueryParams,
    setNavigationStore = _useNavigationStore2[1];
  var shouldShowLevel3Nodes = useCurrentNodeHasMoreLevels(2).hasMoreIssueLevels;
  var shouldShowLevel4Nodes = useCurrentNodeHasMoreLevels(3).hasMoreIssueLevels;
  var shouldShowLevel5Nodes = useCurrentNodeHasMoreLevels(4).hasMoreIssueLevels;
  var shouldShowNextButton = useCallback(function () {
    var hasNotSelectedLevel3Node = shouldShowLevel3Nodes && !level3Node;
    var hasNotSelectedLevel4Node = shouldShowLevel4Nodes && !level4Node;
    var hasNotSelectedLevel5Node = shouldShowLevel5Nodes && !level5Node;
    return !(!level2Node || hasNotSelectedLevel3Node || hasNotSelectedLevel4Node || hasNotSelectedLevel5Node);
  }, [level2Node, level3Node, level4Node, level5Node, shouldShowLevel3Nodes, shouldShowLevel4Nodes, shouldShowLevel5Nodes]);
  var handleTellUsMoreNextButtonClick = function handleTellUsMoreNextButtonClick() {
    goToNextStep();
    trackCxFoundationsTellUsMorePageNextButtonClick(issueType, getRetailerOrBrandToken());
  };
  // If the user has selected a level query param, go to the next step
  useEffect(function () {
    if (shouldShowNextButton() && hasLevelQueryParams) {
      goToNextStepRef.current();
      setNavigationStore({
        hasLevelQueryParams: false
      });
    }
  }, [hasLevelQueryParams, setNavigationStore, shouldShowNextButton]);
  var hasOrderSelected = brandOrders && brandOrders.length > 0 && selectedBrandOrder !== undefined;
  return /*#__PURE__*/React.createElement(Wrapper, {
    "data-test-id": "tell-us-more-wrapper"
  }, /*#__PURE__*/React.createElement(BackButton, null), isMobile ? null : /*#__PURE__*/React.createElement(Spacer, {
    height: "3x"
  }), /*#__PURE__*/React.createElement(Typography, {
    variant: "sectionHeaderSansMedium"
  }, /*#__PURE__*/React.createElement(StrictLocalMsg, {
    id: "SkU2uK",
    defaultMessage: [{
      "type": 0,
      "value": "Dites-nous-en plus"
    }]
  })), /*#__PURE__*/React.createElement(LevelNodesSection, {
    title: hasOrderSelected ? sectionTitles.whatIssue : sectionTitles.whatIssueWithoutOrder
  }, /*#__PURE__*/React.createElement(IssueTopics, {
    parentNode: level1Node,
    selectedNode: level2Node,
    setSelectedNode: setLevel2Node,
    isAutofocusOnMobile: true,
    bottomElementRef: bottomElementRef
  })), /*#__PURE__*/React.createElement(LevelNodesSection, {
    visibleWhen: shouldShowLevel3Nodes,
    title: sectionTitles.chooseCategory
  }, /*#__PURE__*/React.createElement(IssueTopics, {
    parentNode: level2Node,
    selectedNode: level3Node,
    setSelectedNode: setLevel3Node,
    isAutofocusOnMobile: true,
    bottomElementRef: bottomElementRef
  })), /*#__PURE__*/React.createElement(LevelNodesSection, {
    visibleWhen: shouldShowLevel4Nodes,
    title: sectionTitles.chooseType
  }, /*#__PURE__*/React.createElement(IssueTopics, {
    parentNode: level3Node,
    selectedNode: level4Node,
    setSelectedNode: setLevel4Node,
    isAutofocusOnMobile: true,
    bottomElementRef: bottomElementRef
  })), /*#__PURE__*/React.createElement(LevelNodesSection, {
    visibleWhen: shouldShowLevel5Nodes,
    title: sectionTitles.chooseSubType
  }, /*#__PURE__*/React.createElement(IssueTopics, {
    parentNode: level4Node,
    selectedNode: level5Node,
    setSelectedNode: setLevel5Node,
    isAutofocusOnMobile: true,
    bottomElementRef: bottomElementRef
  })), shouldShowNextButton() ? ( /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Spacer, {
    height: "2x"
  }), /*#__PURE__*/React.createElement(Button, {
    onClick: handleTellUsMoreNextButtonClick,
    "data-test-id": "tell-us-more-next-btn"
  }, /*#__PURE__*/React.createElement(StrictLocalMsg, {
    id: "9+Ddtu",
    defaultMessage: [{
      "type": 0,
      "value": "Suivant"
    }]
  })))) : null, /*#__PURE__*/React.createElement(BottomAnchor, {
    ref: bottomElementRef
  }));
};
var Wrapper = styled(Column).attrs({
  align: "flex-start"
}).withConfig({
  displayName: "MoreIssue__Wrapper"
})(["background-color:", ";min-height:100%;"], Core.surface.inverse);
var BottomAnchor = styled.div.withConfig({
  displayName: "MoreIssue__BottomAnchor"
})(["height:100px;"]);